import type { ReactNode } from 'react';
import { clsx } from 'clsx';
import {
  GRID_TABLE_HEADER_CLASS,
  GRID_TABLE_ROW_CLASS,
  TABLE_SCROLL_CONTAINER_CLASS,
} from '@/styles/classes';

type ScrollableGridTableBaseProps = {
  gridClass: string;
  minWidthClass?: string;
  containerClassName?: string;
  header: ReactNode;
  headerClassName?: string;
};

type ScrollableGridTableItemsProps<T> = ScrollableGridTableBaseProps & {
  items: T[];
  getRowKey: (item: T) => string | number;
  renderRow: (item: T) => ReactNode;
  rowClassName?: string | ((item: T) => string);
  rowItemsCenter?: boolean;
  children?: never;
};

type ScrollableGridTableChildrenProps = ScrollableGridTableBaseProps & {
  children: ReactNode;
  items?: never;
  getRowKey?: never;
  renderRow?: never;
  rowClassName?: never;
  rowItemsCenter?: never;
};

type ScrollableGridTableProps<T> =
  | ScrollableGridTableItemsProps<T>
  | ScrollableGridTableChildrenProps;

export function ScrollableGridTable<T>({
  gridClass,
  minWidthClass,
  containerClassName,
  header,
  headerClassName = GRID_TABLE_HEADER_CLASS,
  ...rest
}: ScrollableGridTableProps<T>) {
  const body =
    'children' in rest && rest.children != null ? (
      rest.children
    ) : (
      <ul>
        {rest.items!.map(item => {
          const rowClass =
            typeof rest.rowClassName === 'function'
              ? rest.rowClassName(item)
              : rest.rowClassName ?? GRID_TABLE_ROW_CLASS;

          return (
            <li
              key={rest.getRowKey!(item)}
              className={clsx(
                gridClass,
                rest.rowItemsCenter && 'items-center',
                rowClass
              )}
            >
              {rest.renderRow!(item)}
            </li>
          );
        })}
      </ul>
    );

  return (
    <div className={clsx(TABLE_SCROLL_CONTAINER_CLASS, containerClassName)}>
      <div className={minWidthClass}>
        <div className={clsx(gridClass, headerClassName)}>{header}</div>
        {body}
      </div>
    </div>
  );
}
